import { fetchLocationsMap, getCachedLocationsMap } from '../../../../core/utils/hangoutUtils.js';
import { addSwipeSupport } from '../../../../core/utils/swipe-handlers.js';
import { getBackgroundService } from '../../../../core/services/BackgroundService.js';
import { createElement, getSettingsModalTranslationText } from './settings-dom-utils.js';
import {
    mapTimeOfDayToValue
} from './settings-state.js';

let settingsBackgroundPreviewRefresh = null;

export const refreshSettingsBackgroundPreview = () => {
    if (typeof settingsBackgroundPreviewRefresh !== 'function') return undefined;
    return settingsBackgroundPreviewRefresh();
};

const setPreviewMessage = (preview, text, isError = false) => {
    preview.replaceChildren();
    const status = createElement('span', `settings-preview-status${isError ? ' is-error' : ''}`, {
        textContent: text
    });
    preview.appendChild(status);
};

const setPreviewImage = (preview, imageUrl, alt) => {
    preview.replaceChildren();
    const img = createElement('img', 'settings-preview-img', { src: imageUrl, alt });
    img.draggable = false;
    preview.appendChild(img);
};

export const createBackgroundSection = (currentState) => {
    const backgroundSection = createElement('div', 'form-group');
    const backgroundTitle = createElement('h3', '', { textContent: 'Background Selection' });
    backgroundTitle.setAttribute('data-translate', 'settings_modal.background_selection');
    backgroundSection.appendChild(backgroundTitle);

    const backgroundRow = createElement('div', 'settings-bg-row');
    const backgroundGroup = createElement('div', 'form-group settings-bg-group');
    const backgroundSelectContainer = createElement('div', 'settings-bg-selects');

    const backgroundSearchRoot = createElement('div', 'search-root settings-bg-search', { 'data-search-root': 'generate-bg-settings' });
    const backgroundSelect = createElement('select', 'settings-bg-select', {
        id: 'generate-bg-settings',
        name: 'generate-bg-settings'
    });
    backgroundSelect.innerHTML = '<option value="" disabled selected>Select a background</option>';
    const firstBackgroundOption = backgroundSelect.querySelector('option');
    if (firstBackgroundOption) {
        firstBackgroundOption.setAttribute('data-translate', 'settings_modal.select_background');
    }

    const timeSearchRoot = createElement('div', 'search-root settings-time-search', { 'data-search-root': 'time-of-day-settings' });
    const timeOfDaySelect = createElement('select', 'settings-time-select', { id: 'time-of-day-settings' });
    timeOfDaySelect.innerHTML = [
        '<option value="0.1" data-translate="settings_modal.time_day">Day</option>',
        '<option value="0.2" data-translate="settings_modal.time_sunset">Sunset</option>',
        '<option value="0.3" data-translate="settings_modal.time_night">Night</option>'
    ].join('');

    const currentTimeOfDay = mapTimeOfDayToValue(currentState.timeOfDay);
    timeOfDaySelect.value = currentTimeOfDay;

    backgroundSelectContainer.appendChild(backgroundSearchRoot);
    backgroundSelectContainer.appendChild(backgroundSelect);
    backgroundSelectContainer.appendChild(timeSearchRoot);
    backgroundSelectContainer.appendChild(timeOfDaySelect);

    const backgroundPreview = createElement('div', 'settings-bg-preview', { id: 'bg-preview-settings' });
    const placeholder = createElement('span', 'settings-preview-placeholder', {
        textContent: 'Background preview will appear here'
    });
    placeholder.setAttribute('data-translate', 'settings_modal.background_preview_placeholder');
    backgroundPreview.appendChild(placeholder);

    const backgroundPreviewWrapper = createElement('div', 'background-preview-wrapper');

    const bgLeftArrow = createElement('button', 'preview-arrow preview-arrow--bg preview-arrow--left', {
        id: 'bg-arrow-left',
        type: 'button',
        'aria-label': 'Previous location',
        tabindex: '0'
    });
    bgLeftArrow.setAttribute('data-translate-attr', 'aria-label:settings_modal.previous_location');
    bgLeftArrow.innerHTML = '<i class="fas fa-chevron-left"></i>';

    const bgRightArrow = createElement('button', 'preview-arrow preview-arrow--bg preview-arrow--right', {
        id: 'bg-arrow-right',
        type: 'button',
        'aria-label': 'Next location',
        tabindex: '0'
    });
    bgRightArrow.setAttribute('data-translate-attr', 'aria-label:settings_modal.next_location');
    bgRightArrow.innerHTML = '<i class="fas fa-chevron-right"></i>';

    backgroundPreviewWrapper.appendChild(bgLeftArrow);
    backgroundPreviewWrapper.appendChild(backgroundPreview);
    backgroundPreviewWrapper.appendChild(bgRightArrow);

    backgroundGroup.appendChild(backgroundSelectContainer);
    backgroundGroup.appendChild(backgroundPreviewWrapper);
    backgroundRow.appendChild(backgroundGroup);
    backgroundSection.appendChild(backgroundRow);

    return {
        backgroundSection,
        backgroundSelect,
        timeOfDaySelect,
        backgroundPreview,
        bgLeftArrow,
        bgRightArrow
    };
};

export async function loadBackgroundOptions(backgroundSelect, timeOfDaySelect, backgroundPreview, bgLeftArrow, bgRightArrow) {
    let updateBackgroundPreview = null;

    try {
        let data = await fetchLocationsMap();
        if (!data || Object.keys(data).length === 0) {
            data = getCachedLocationsMap();
        }

        const selectBackgroundText = getSettingsModalTranslationText('select_background', 'Select a background');
        const loadingBackgroundText = getSettingsModalTranslationText('loading_background', 'Loading background...');
        const previewAltText = getSettingsModalTranslationText('background_preview_alt', 'Background preview');
        const failedLoadBackgroundPreviewText = getSettingsModalTranslationText('failed_load_background_preview', 'Failed to load background preview');

        backgroundSelect.innerHTML = `<option value="" disabled selected>${selectBackgroundText}</option>`;

        Object.entries(data).forEach(([id, name]) => {
            const displayName = typeof name === 'object' ? name.name : name;
            const option = createElement('option', '', { value: id, textContent: displayName });
            backgroundSelect.appendChild(option);
        });

        let previewToken = 0;
        updateBackgroundPreview = async () => {
            const requestToken = ++previewToken;
            const backgroundId = backgroundSelect.value;
            const timeOfDay = timeOfDaySelect.value;
            if (!backgroundId) return;

            const backgroundService = getBackgroundService();
            setPreviewMessage(backgroundPreview, loadingBackgroundText);

            try {
                const imageUrl = await backgroundService.resolveExistingBackgroundUrl(backgroundId, timeOfDay);
                if (requestToken !== previewToken) return;
                setPreviewImage(backgroundPreview, imageUrl, previewAltText);
            } catch {
                if (requestToken !== previewToken) return;
                setPreviewMessage(backgroundPreview, failedLoadBackgroundPreviewText, true);
            }
        };
        settingsBackgroundPreviewRefresh = updateBackgroundPreview;

        backgroundSelect.addEventListener('change', updateBackgroundPreview);
        timeOfDaySelect.addEventListener('change', updateBackgroundPreview);

        const getSelectableOptions = (selectEl) =>
            Array.from(selectEl.options).filter(opt => opt && !opt.disabled && opt.value);

        const cycleSelect = (selectEl, direction) => {
            const options = getSelectableOptions(selectEl);
            if (!options.length) return;
            const currentValue = selectEl.value;
            let currentIndex = options.findIndex(opt => opt.value === currentValue);
            if (currentIndex < 0) currentIndex = 0;
            const nextIndex = (currentIndex + direction + options.length) % options.length;
            const nextValue = options[nextIndex]?.value;
            if (!nextValue || nextValue === selectEl.value) return;
            selectEl.value = nextValue;
            selectEl.dispatchEvent(new Event('change'));
        };

        const handleCycleLocation = (direction) => cycleSelect(backgroundSelect, direction);

        const attachKeyHandler = (button, direction, handler) => {
            button.addEventListener('click', () => handler(direction));
            button.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handler(direction);
                }
            });
        };

        attachKeyHandler(bgLeftArrow, -1, handleCycleLocation);
        attachKeyHandler(bgRightArrow, 1, handleCycleLocation);

        addSwipeSupport(backgroundPreview, (direction) => {
            handleCycleLocation(direction === 'left' ? -1 : 1);
        });

    } catch (error) {
        console.error('Error loading background options:', error);
        const failedLoadBackgroundsText = getSettingsModalTranslationText('failed_load_backgrounds', 'Failed to load backgrounds');
        backgroundSelect.innerHTML = `<option value="" disabled>${failedLoadBackgroundsText}</option>`;
    }

    return updateBackgroundPreview;
}
